import { useState, useEffect } from 'react'
import { TestTube, CheckCircle, XCircle, Save, Trash2, Loader2, Eye, EyeOff, Link2 } from 'lucide-react'
import { api } from '../lib/api'
import toast from 'react-hot-toast'

interface NextcloudCredential {
  id: number
  nc_username: string
  nc_base_url?: string | null
  enabled: boolean
  last_tested?: string | null
  test_status?: 'untested' | 'success' | 'failed' | null
  test_error_message?: string | null
}

interface NextcloudCredentialsPanelProps {
  adminConfigured: boolean
}

export default function NextcloudCredentialsPanel({ adminConfigured }: NextcloudCredentialsPanelProps) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [linking, setLinking] = useState(false)
  const [credential, setCredential] = useState<NextcloudCredential | null>(null)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [lernraketePassword, setLernraketePassword] = useState('')
  const [personalBaseUrl, setPersonalBaseUrl] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [accountModeDescription, setAccountModeDescription] = useState<string | null>(null)
  const [allowPersonalNextcloud, setAllowPersonalNextcloud] = useState<boolean | null>(null)
  const [schoolBaseUrl, setSchoolBaseUrl] = useState<string | null>(null)
  const [useLernraketeCredentials, setUseLernraketeCredentials] = useState(false)

  const loadStatus = async () => {
    try {
      const res = await api.get('/nextcloud/status')
      const data = res.data?.data
      setAccountModeDescription(data?.accountModeDescription || null)
      setAllowPersonalNextcloud(data?.allowPersonalNextcloud ?? null)
      setSchoolBaseUrl(data?.schoolBaseUrl || null)
      setUseLernraketeCredentials(!!data?.useLernraketeCredentials)
    } catch {
      /* optional */
    }
  }

  const load = async () => {
    setLoading(true)
    try {
      await loadStatus()
      const res = await api.get('/nextcloud/credentials')
      const data = res.data?.data as NextcloudCredential | null
      setCredential(data)
      if (data?.nc_username) setUsername(data.nc_username)
      if (data?.nc_base_url) setPersonalBaseUrl(data.nc_base_url)
      else setPersonalBaseUrl('')
    } catch {
      toast.error('Nextcloud-Zugangsdaten konnten nicht geladen werden')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const handleLinkLernrakete = async () => {
    if (!lernraketePassword) {
      toast.error('Bitte Ihr Lernrakete-Passwort eingeben')
      return
    }
    setLinking(true)
    try {
      const res = await api.post('/nextcloud/credentials/lernrakete', { password: lernraketePassword })
      const msg = res.data?.message || 'Nextcloud mit Lernrakete-Zugangsdaten verbunden'
      toast.success(msg)
      setLernraketePassword('')
      await load()
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } } }
      toast.error(e.response?.data?.error || 'Verbindung fehlgeschlagen')
    } finally {
      setLinking(false)
    }
  }

  const handleSave = async () => {
    if (!adminConfigured) {
      toast.error('Nextcloud ist noch nicht durch die Administration eingerichtet')
      return
    }
    if (!username.trim() || (!credential && !password)) {
      toast.error('Benutzername und App-Passwort sind erforderlich')
      return
    }
    setSaving(true)
    try {
      const body: { nc_username: string; nc_password?: string; nc_base_url?: string } = {
        nc_username: username.trim()
      }
      if (password) body.nc_password = password
      if (allowPersonalNextcloud) {
        body.nc_base_url = personalBaseUrl.trim()
      }
      await api.post('/nextcloud/credentials', body)
      toast.success('Nextcloud-Zugangsdaten gespeichert')
      setPassword('')
      await load()
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } } }
      toast.error(e.response?.data?.error || 'Speichern fehlgeschlagen')
    } finally {
      setSaving(false)
    }
  }

  const handleTest = async () => {
    setTesting(true)
    try {
      const body: { nc_username?: string; nc_password?: string; nc_base_url?: string } = {}
      if (useLernraketeCredentials && lernraketePassword) {
        const res = await api.post('/nextcloud/credentials/lernrakete', { password: lernraketePassword })
        toast.success(res.data?.message || 'Verbindung zu Nextcloud erfolgreich')
        setLernraketePassword('')
        await load()
        return
      }
      if (password) {
        body.nc_username = username.trim()
        body.nc_password = password
      }
      if (allowPersonalNextcloud) {
        body.nc_base_url = personalBaseUrl.trim()
      }
      await api.post('/nextcloud/test', body)
      toast.success('Verbindung zu Nextcloud erfolgreich')
      await load()
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } } }
      toast.error(e.response?.data?.error || 'Verbindungstest fehlgeschlagen')
      await load()
    } finally {
      setTesting(false)
    }
  }

  const handleDelete = async () => {
    if (!confirm('Nextcloud-Zugangsdaten wirklich entfernen?')) return
    try {
      await api.delete('/nextcloud/credentials')
      toast.success('Zugangsdaten entfernt')
      setCredential(null)
      setUsername('')
      setPassword('')
      setPersonalBaseUrl('')
    } catch {
      toast.error('Löschen fehlgeschlagen')
    }
  }

  if (loading) {
    return (
      <div className="text-center py-6">
        <Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" />
        <p className="mt-2 text-sm text-gray-500">Lade Nextcloud-Einstellungen…</p>
      </div>
    )
  }

  if (useLernraketeCredentials) {
    return (
      <div className="space-y-4">
        {!adminConfigured && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3 dark:bg-amber-950/40 dark:border-amber-800 dark:text-amber-300">
            Die Nextcloud-Anbindung wurde noch nicht durch die Administration eingerichtet.
          </p>
        )}
        {accountModeDescription && (
          <p className="text-sm text-sky-800 bg-sky-50 border border-sky-200 rounded-md p-3 dark:bg-sky-950/40 dark:border-sky-800 dark:text-sky-200">
            {accountModeDescription}
          </p>
        )}
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Ihre Schule nutzt dieselben Zugangsdaten wie beim Lernrakete-Login. Beim Anmelden wird die Nextcloud-Verbindung automatisch eingerichtet.
          Bei Zwei-Faktor-Authentifizierung bestätigen Sie die Verbindung einmalig mit Ihrem Lernrakete-Passwort.
        </p>
        {credential?.test_status && (
          <div className={`flex items-center text-sm ${
            credential.test_status === 'success' ? 'text-green-600' : credential.test_status === 'failed' ? 'text-red-600' : 'text-gray-500'
          }`}>
            {credential.test_status === 'success' && <CheckCircle className="w-4 h-4 mr-1" />}
            {credential.test_status === 'failed' && <XCircle className="w-4 h-4 mr-1" />}
            {credential.test_status === 'success' && `Verbunden als ${credential.nc_username}`}
            {credential.test_status === 'failed' && (credential.test_error_message || 'Verbindung fehlgeschlagen')}
            {credential.test_status === 'untested' && 'Noch nicht verbunden – bitte Passwort bestätigen'}
          </div>
        )}
        {!credential?.test_status || credential.test_status !== 'success' ? (
          <div className="max-w-md space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lernrakete-Passwort zur Bestätigung</label>
              <input
                type="password"
                value={lernraketePassword}
                onChange={(e) => setLernraketePassword(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                disabled={!adminConfigured || linking}
              />
            </div>
            <button
              type="button"
              onClick={handleLinkLernrakete}
              disabled={!adminConfigured || linking || !lernraketePassword}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {linking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
              Mit Lernrakete-Zugangsdaten verbinden
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={handleDelete}
            className="inline-flex items-center gap-2 px-4 py-2 text-red-600 text-sm font-medium rounded-md hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" />
            Verbindung trennen
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {!adminConfigured && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3 dark:bg-amber-950/40 dark:border-amber-800 dark:text-amber-300">
          Die Nextcloud-Anbindung wurde noch nicht durch die Administration eingerichtet. Bitte wenden Sie sich an Ihre IT-Verwaltung.
        </p>
      )}

      {accountModeDescription && (
        <p className="text-sm text-sky-800 bg-sky-50 border border-sky-200 rounded-md p-3 dark:bg-sky-950/40 dark:border-sky-800 dark:text-sky-200">
          {accountModeDescription}
        </p>
      )}

      {credential?.test_status && (
        <div className={`flex items-center text-sm ${
          credential.test_status === 'success' ? 'text-green-600' : credential.test_status === 'failed' ? 'text-red-600' : 'text-gray-500'
        }`}>
          {credential.test_status === 'success' && <CheckCircle className="w-4 h-4 mr-1" />}
          {credential.test_status === 'failed' && <XCircle className="w-4 h-4 mr-1" />}
          {credential.test_status === 'success' && 'Letzter Verbindungstest erfolgreich'}
          {credential.test_status === 'failed' && (credential.test_error_message || 'Letzter Verbindungstest fehlgeschlagen')}
          {credential.test_status === 'untested' && 'Noch nicht getestet'}
        </div>
      )}

      <div className="grid gap-4 max-w-md">
        {allowPersonalNextcloud && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Eigene Nextcloud-Server-URL (optional)
            </label>
            <input
              type="url"
              value={personalBaseUrl}
              onChange={(e) => setPersonalBaseUrl(e.target.value)}
              placeholder={schoolBaseUrl ? `Leer = ${schoolBaseUrl}` : 'https://meine-cloud.de'}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              disabled={!adminConfigured}
            />
            <p className="mt-1 text-xs text-gray-500">
              Leer lassen, um den Schul-Server zu nutzen.
            </p>
          </div>
        )}
        {!allowPersonalNextcloud && schoolBaseUrl && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Server: <strong>{schoolBaseUrl}</strong>
          </p>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nextcloud-Benutzername</label>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="z. B. vorname.nachname"
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            disabled={!adminConfigured}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            App-Passwort {credential ? '(leer lassen, um unverändert zu lassen)' : ''}
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="App-Passwort aus Nextcloud"
              className="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              disabled={!adminConfigured}
            />
            <button
              type="button"
              onClick={() => setShowPassword((p) => !p)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500"
              aria-label={showPassword ? 'Verbergen' : 'Anzeigen'}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Erstellen Sie unter Nextcloud → Einstellungen → Sicherheit ein App-Passwort.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={!adminConfigured || saving || !username.trim() || (!credential && !password)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Speichern
        </button>
        <button
          type="button"
          onClick={handleTest}
          disabled={!adminConfigured || testing || (!credential && (!username.trim() || !password))}
          className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200"
        >
          {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <TestTube className="h-4 w-4" />}
          Verbindung testen
        </button>
        {credential && (
          <button
            type="button"
            onClick={handleDelete}
            className="inline-flex items-center gap-2 px-4 py-2 text-red-600 text-sm font-medium rounded-md hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" />
            Entfernen
          </button>
        )}
      </div>
    </div>
  )
}
